/**
 * Condition evaluator
 * Evaluates choice conditions based on game state
 */

import { SaveStateManager } from '../../../features/save-state'
import { ITEM_NAMES, ITEMS_NEEDED_TO_PASS } from '../../../features/minigames/baleni-s-rychlonozkou/constants'
import type { ChoiceCondition } from '../types'

/**
 * Evaluate a choice condition
 */
export function evaluateCondition(condition: ChoiceCondition): boolean {
  const savedState = SaveStateManager.load()
  if (!savedState) return false

  // Check if condition uses type-based format (new format)
  if ('type' in condition) {
    switch (condition.type) {
      case 'inventory': {
        const consumed = savedState.consumedInventory || []
        if (consumed.includes(condition.item)) {
          return false
        }
        const inventory = savedState.inventory || []
        if (inventory.includes(condition.item)) {
          return true
        }
        // Fallback: check minigame selected items (for baleni-s-rychlonozkou)
        const minigames = savedState.minigames || {}
        const minigame = minigames['minihra-baleni-s-rychlonozkou']
        if (minigame?.selectedItems?.includes(condition.item)) {
          return true
        }
        return false
      }
      case 'flag': {
        const flagValue = savedState.flags?.[condition.flag] || false
        return condition.value !== undefined ? flagValue === condition.value : flagValue
      }
      case 'choice': {
        const choices = savedState.choices || {}
        return choices[condition.sceneId] === condition.choiceNumber
      }
      case 'not': {
        return !evaluateCondition(condition.condition)
      }
      case 'and': {
        return condition.conditions.every((c) => evaluateCondition(c))
      }
      case 'or': {
        return condition.conditions.some((c) => evaluateCondition(c))
      }
      default:
        console.warn('Unknown condition type:', (condition as any).type)
        return false
    }
  }

  // Legacy format support (backward compatibility)
  // Check if condition is a flag
  if ('flag' in condition && condition.flag) {
    return savedState.flags?.[condition.flag] === true
  }

  // Check if condition is an item
  if ('item' in condition && condition.item) {
    const inventory = savedState.inventory || []
    return inventory.includes(condition.item)
  }

  // Check if condition is a scene visited
  if (condition.sceneVisited) {
    const visitedScenes = savedState.visitedScenes || []
    return visitedScenes.includes(condition.sceneVisited)
  }

  // Check if condition is a choice made
  if (condition.choice) {
    const choices = savedState.choices || {}
    return choices[condition.choice.sceneId] === condition.choice.choiceNumber
  }

  // Check if condition is an item count
  if (condition.itemCount) {
    const inventory = savedState.inventory || []
    const count = inventory.filter((item) => item === condition.itemCount!.item).length
    return count >= (condition.itemCount.min || 0) && count <= (condition.itemCount.max || Infinity)
  }

  // Check if condition is a minigame passed
  if (condition.minigamePassed) {
    const minigameResults = savedState.minigameResults || {}
    return minigameResults[condition.minigamePassed] === true
  }

  // Check if condition is a specific minigame item count (for baleni-s-rychlonozkou)
  if (condition.minigameItemCount) {
    const minigameItems = savedState.minigameItems || {}
    const sceneItems = minigameItems[condition.minigameItemCount.sceneId] || []
    const itemName = condition.minigameItemCount.item
    const count = sceneItems.filter((item) => item === itemName).length
    const minNeeded = ITEMS_NEEDED_TO_PASS[itemName as keyof typeof ITEMS_NEEDED_TO_PASS] || 0
    return count >= minNeeded
  }

  // Check if condition is a combination (AND/OR)
  if (condition.and) {
    return condition.and.every((c) => evaluateCondition(c))
  }

  if (condition.or) {
    return condition.or.some((c) => evaluateCondition(c))
  }

  // Check if condition is a negation
  if (condition.not) {
    return !evaluateCondition(condition.not)
  }

  // Unknown condition type
  console.warn('Unknown condition type:', condition)
  return false
}
